/**
 * What the plan selector shows, and where choosing something in it goes.
 *
 * Leo, 2026-09-20: *"the ui for multiple plans is not good, right now there's a single plan menu
 * option and I go there to see my running plan or add a new one. Each plan should be fully
 * isolated, I want a plan drop down select at the top of the ui, it shows you your plans and if
 * you have room for one more it lets you choose add one."*
 *
 * **Every decision in this file fails silently when it is wrong**, which is why it is here
 * rather than inline in the component: `npm test` does not type-check `.tsx` at all, so a rule
 * written in a component is a rule nothing in this repository can assert. A selector offering a
 * plan slot that does not exist is a refusal a learner reaches after a paragraph of typing; one
 * that hides the offer is a feature somebody is paying for with no way to discover it — the bug
 * `plan/room.ts` exists to close; one that names a plan the screen is not showing is the app
 * telling a learner they are somewhere they are not. None of those throws, logs, or reddens a
 * build.
 *
 * React-free on purpose, like `nav.ts`, `tabs.ts` and `plan/room.ts`.
 */

/* `.js` extensions throughout: this module is compiled for plain Node as well as for the
   bundler, and Node's ESM resolver does not guess them. */
import { ROUTES } from '../routes.js';
import { PLAN_SCREENS, orderedPlans, planPath, type TabObjective } from '../tabs.js';
import type { Offer } from './room.js';

/**
 * The value the *add a plan* option carries, and the one the control shows while a plan is
 * being stated.
 *
 * A sentinel inside the select rather than a second control beside it, because on a phone the
 * select is a native sheet and *add a plan* belongs in the same sheet as the plans: a learner
 * looking for where a second plan would live opens the list of plans.
 *
 * **One value for both, because they are one place.** The option that offers the clean slate and
 * the option that says you are standing on it are the same destination, `ROUTES.newPlan`, wearing
 * two labels; two sentinels would be two spellings of one address, which is what `routes.ts`
 * exists to prevent. The label is the component's (`plans.add` and `plans.new`) and the
 * destination is `planChoice`'s.
 *
 * **It cannot collide with an `objectiveId`, twice over.** It is not `obj_`-prefixed, which is
 * what `id('obj')` mints; and `planChoice` maps it to a *path* rather than to `?o=`, so unlike
 * every other value in this control it never reaches the parameter an id is spelled in at all.
 * `plan-selector.test.ts` asserts both halves.
 *
 * Renamed from `ADD_ONE` when the destination stopped being a fragment on the plan screen: it
 * names a state of the app now, not an action taken inside a plan.
 */
export const NEW_PLAN = '__new__';

/**
 * The state the selector renders, and it has three because it means three different things.
 *
 * **`name` is the decision worth arguing about.** `objectiveTabs` drew nothing at all below two
 * plans, on the grounds that a single tab is a permanent untappable control naming the only
 * thing it could name. That argument survives for a *control* and not for a *label*: the bar
 * under the selector is plan-scoped navigation now, and with nothing naming the plan above it
 * there is nothing on the screen saying what *plan · progress · chat* are about. So a learner
 * whose one plan is their only reachable plan gets the plan's name as text. It costs no tap
 * target, it cannot be a dead control, and it is the label the previous shell never had.
 *
 * The moment a second plan is reachable — because one exists, or because there is room to state
 * one — the name becomes a real select, which is what the owner asked for.
 *
 * **Who is actually in each state today, derived rather than assumed.** `ENTITLEMENTS` grants
 * `activeObjectives: 1` at tier 1, 2 at tier 2, 3 at tier 3 and **3 at the internal tier 99**,
 * which is what `NABU_DEFAULT_TIER` is set to in every stack and therefore what every account
 * with no `tier` resolves to. So the live answer for a learner with one plan is a **select**
 * holding that plan and *add a plan* — which is the owner's request working, not a fallback.
 * `name` is reached by a tier-1 learner with their one plan, and by anybody sitting at their
 * cap; a review of this file's first draft caught it claiming `name` was what everybody saw,
 * on a cap of one that was raised to three on 2026-09-19.
 */
export type Selector =
  /** Nothing to say: the plans have not been read, or there is no plan at all. */
  | { kind: 'hidden' }
  /** One plan and nowhere else to go. Its name, as text, not as a control. */
  | { kind: 'name'; objectiveId: string; label: string }
  /** A real choice. */
  | {
      kind: 'select';
      options: readonly PlanOption[];
      /**
       * The option to show as chosen, or `''` when the address names a plan that is not live.
       *
       * **`''` is a state and not a missing value**, and it is `objectiveTabs`'s rule carried
       * over: a `?o=` outlives the plan it names — an old notification, a plan closed on another
       * device — and the screens do not agree about what to render for a dead one (the chat
       * reads that thread, the plan screen falls back to the API's default). A select *must*
       * show something, so unlike the old bar it cannot simply mark nothing; it shows a
       * placeholder saying so instead. Naming a live plan the learner is not looking at would be
       * the control lying about where they are.
       */
      value: string;
      /** Whether to draw that placeholder. True exactly when `value` is `''`. */
      stray: boolean;
      /**
       * Whether the learner is on the clean slate, and the control has to say so.
       *
       * **This is the owner's *"the selector says where you are"*.** `/new-plan` shows nothing
       * belonging to any plan, so the selector is the only thing on the screen that can place
       * the learner — and it is also the only way off it that does not involve creating
       * something. It therefore draws an entry for the state itself, marked as the chosen one,
       * above the plans they can go back to.
       *
       * **It forces `kind: 'select'`**, even for the learner whose single plan would otherwise
       * be a label. A label is not a way back, and with the plan bar hidden (see `showsPlanNav`)
       * a label here would leave the wordmark as the only exit — a learner one tap from their
       * plan on every other screen, stranded on this one. `planSelector` asserts that by
       * construction; `plan-selector.test.ts` asserts it as behaviour.
       *
       * True implies `value === NEW_PLAN`, `stray === false` and `addOne === false`.
       */
      creating: boolean;
      /**
       * Whether *add a plan* is offered. See `roomForAnother` — it degrades towards offering.
       *
       * Never true while `creating` is: an offer to go where you already are is a dead entry,
       * and the owner asked for the selector not to offer it from inside the clean slate.
       */
      addOne: boolean;
    };

export interface PlanOption {
  /** The `objectiveId`. */
  value: string;
  /** What it is called, already localised and cut. See `tabLabel` and `optionLabel`. */
  label: string;
}

/**
 * What to draw, given the plans, the plan on screen and whether there is room for another.
 *
 * `offer` is `roomForAnother`'s answer and is passed in rather than recomputed, so there is one
 * decision about the cap in the browser and the selector cannot disagree with the plan screen
 * about whether a slot exists. That matters in both directions: a selector offering *add one*
 * into a screen that then refuses is the refusal a learner reaches, and a screen offering a form
 * the selector hides is the bug `plan/room.ts` was written for.
 */
export function planSelector(input: {
  /** Every live plan, as `GET /api/objectives/current` answers. `undefined` until it has. */
  plans: readonly TabObjective[] | undefined;
  /** The plan on screen: `?o=`, or the plan the API defaulted to. */
  selected: string | undefined;
  /** Whether another plan may be stated. */
  offer: Offer;
  /**
   * Whether the learner is on the clean slate — `pathname === ROUTES.newPlan`.
   *
   * The address and not a flag somebody sets, so a reload and a shared link answer the same way
   * as a tap. See `Selector`'s `creating` for what it changes and why it changes it.
   */
  creating?: boolean;
  /** What to call a plan. Localisation and truncation live in the component. */
  labelOf: (plan: TabObjective, index: number) => string;
}): Selector {
  /*
   * Nothing at all until the plans have been read, and `undefined` is not an empty list.
   *
   * A selector that appears and then changes shape is the flicker `roomForAnother` refuses to
   * cause for the create form, and it is worse here: this sits in the fixed header, so a
   * late-arriving row moves every screen in the app down a beat after it painted.
   */
  if (input.plans === undefined) return { kind: 'hidden' };

  const ordered = orderedPlans(input.plans);
  const addOne = input.offer.offer;

  /*
   * No plans at all is the create form's own screen, and it is the whole of it.
   *
   * A control whose only option is "go to the screen you are already on" is a dead control, and
   * `Landing` already sends a learner with no objective to `/plan`. Nothing is the truthful
   * answer here: there is no plan to be scoped to, so there is no scope to name.
   */
  if (ordered.length === 0) return { kind: 'hidden' };

  const options = ordered.map((plan, index) => ({
    value: plan.objectiveId,
    label: input.labelOf(plan, index),
  }));

  /*
   * Stating a plan: the control names that, and lists the plans as the way back.
   *
   * **Before the label case below, and that order is the decision.** A learner with one plan and
   * no room would otherwise get their plan's name as text — no control, and with the plan bar
   * hidden on this screen, no way back to it at all. It is also how somebody at their cap who
   * typed this address or followed a stale link gets out of it: they are told they are creating
   * (by the entry marked chosen) and handed every plan they have.
   *
   * `addOne` is dropped rather than passed through, whatever `roomForAnother` said: an entry
   * offering to take a learner where they already are is a dead control, and the owner asked for
   * the selector not to offer it from here.
   */
  if (input.creating) {
    return { kind: 'select', options, value: NEW_PLAN, stray: false, creating: true, addOne: false };
  }

  if (options.length === 1 && !addOne) {
    const only = options[0]!;
    return { kind: 'name', objectiveId: only.value, label: only.label };
  }

  const live = options.some((option) => option.value === input.selected);
  return {
    kind: 'select',
    options,
    value: live ? input.selected! : '',
    stray: !live,
    creating: false,
    addOne,
  };
}

/**
 * Whether two readings of the plans would draw the same selector.
 *
 * **It exists to stop a poll re-rendering the app four times a minute.** `Chat` re-reads its
 * plans every twenty seconds and hands `PlansProvider` a fresh array each time, and the provider
 * wraps the router — so without this, every tick is a new context value and a re-render of the
 * shell, the header and the screen, for a row of chrome in which nothing moved. `Chat`'s own
 * poll path already makes exactly this comparison before it sets its own state; this is the same
 * care on the path that feeds the selector.
 *
 * **The fields compared are the ones the selector draws**, and no others. `objectiveId` and
 * `createdAt` decide which options there are and in what order, `title` and `description` decide
 * what each says, `unread` decides the number after it, and **`status` decides whether a plan is
 * listed at all** — the caller filters with `isLiveObjective` *after* this, so a plan closing is
 * a change to the control even though nothing else about it moved. Leaving `status` out is a
 * closed plan stranded in the list until something else happened to change: the first draft of
 * this function did exactly that, and its own docblock claimed otherwise.
 *
 * A plan's curriculum or its target changing is not a change to this control, and treating it as
 * one would put the re-render back.
 *
 * **`undefined` is not equal to anything, itself included**, which is the rule the rest of this
 * file keeps: a list nobody has read is not the same state as an empty one, and collapsing them
 * here would let a failed read masquerade as "nothing changed".
 */
export function samePlans(
  a: readonly PlanLikeness[] | undefined,
  b: readonly PlanLikeness[] | undefined,
): boolean {
  if (a === undefined || b === undefined) return false;
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((one, index) => {
    const other = b[index]!;
    return (
      one.objectiveId === other.objectiveId &&
      one.createdAt === other.createdAt &&
      one.description === other.description &&
      one.unread === other.unread &&
      one.status === other.status &&
      sameTitle(one.title, other.title)
    );
  });
}

/**
 * What `samePlans` needs, which is a `TabObjective` plus the one field that decides membership.
 *
 * `status` is optional because `TabObjective` — the shape the React-free rules are written
 * against — does not carry one; `Objective` in `api.ts` does, and that is what the provider
 * actually holds. Optional rather than required so this stays usable from a test that does not
 * care, and compared unconditionally so a real one is never ignored.
 */
export type PlanLikeness = TabObjective & { status?: string };

/** A plan's generated name, per locale, compared by what it actually holds. */
function sameTitle(a: Record<string, string> | undefined, b: Record<string, string> | undefined) {
  if (a === b) return true;
  if (!a || !b) return false;
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((key) => a[key] === b[key]);
}

/**
 * What a plan's option says, when the API could say how many questions are waiting in it.
 *
 * **Absent is not nought** (see `TabObjective.unread`), so a plan whose count could not be taken
 * wears its name alone rather than a nought claiming its thread is clear. Nought itself also
 * draws nothing: a "(0)" beside every plan is noise on the one control a learner reads on every
 * screen.
 *
 * The number rides in the option's own text rather than beside the control, and that is forced
 * rather than chosen: an `<option>` is text to the platform — no element, no pill, no markup a
 * native picker will draw — so a count rendered as an element would simply not exist on a phone.
 * Parentheses because that is how a count reads aloud without a word for it, and because the
 * visible width of a collapsed select on a phone is about three words.
 */
export function optionLabel(label: string, unread: number | undefined): string {
  return typeof unread === 'number' && unread > 0 ? `${label} (${unread})` : label;
}

/**
 * Where choosing something in the selector goes.
 *
 * **Switching plan keeps the screen**, which is `sectionHref`'s promise read from the other
 * side: a learner comparing two plans' progress presses the selector and stays on progress.
 *
 * **And from the clean slate it goes to the plan, which is how that screen is left.** `/new-plan`
 * is not a `PLAN_SCREEN`, so it falls into the same branch the global screens do and lands on
 * `/plan?o=<chosen>` — the owner's *"going back to any existing plan fills that context with that
 * plan only"*. Nothing of the half-written objective travels with it: the address names the plan,
 * and `App.tsx` gives the two routes different keys so the screen is built again from nothing
 * rather than reconciled out of the one being left.
 *
 * **From a global screen it goes to the plan.** Configuration and the profile carry no `?o=` by
 * the owner's decision — *"Messaging settings is global, I define globally, NOT per objective"*
 * — so there is no such thing as "this screen, that plan" for them, and leaving the learner
 * where they are would make the selector a control with no visible effect. `/plan` is where
 * choosing a plan means something, and it is the first section of the bar underneath.
 *
 * **`add a plan` goes to the clean slate**, which is a screen of its own since 2026-09-20.
 *
 * Leo: *"'Learn something else too' must not be in the current plan, that is confusing… when I'm
 * in a plan everything is about that plan, when I add a new one it's a clean slate."* It used to
 * answer `/plan#another` — the current plan's entire screen, curriculum, research, progress and
 * all, with one section scrolled into view — so the learner was still inside plan A while being
 * asked to describe plan B. The form is still defined exactly once (`stateAnObjective` in
 * `Objectives.tsx`); what changed is that the screen around it holds nothing else.
 *
 * **No plan in the address, and now not even a plan screen.** Stating an objective is not an act
 * on the plan being looked at, and an address that names a plan is a claim the screen is about it
 * — which is why this one is `ROUTES.newPlan` and carries no `?o=`. The `live` count is gone with
 * the fragment: there is one destination for this value at every number of plans, and the case it
 * used to branch for (no plan at all) never reaches here, because `planSelector` draws nothing at
 * all for a learner with no plans.
 *
 * **It is also where the *you are here* entry points**, which is the same value and therefore the
 * same address — a no-op navigation, which is what choosing where you already are should be.
 */
export function planChoice(input: {
  /** The value the select handed back: an `objectiveId`, or `NEW_PLAN`. */
  value: string;
  /** The screen the learner is on. */
  pathname: string;
}): string {
  if (input.value === NEW_PLAN) return ROUTES.newPlan;
  const path = PLAN_SCREENS.includes(input.pathname) ? input.pathname : ROUTES.plan;
  return planPath(path, input.value);
}
